/**
 * The MIT License (MIT)
 *
 * Copyright (c) 2016 Michał Osadnik osdnk
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { useMemo } from 'react';
import { Platform } from 'react-native';
import { withTiming } from 'react-native-reanimated';
import { Gesture, State } from 'react-native-gesture-handler';
import { triggerChartSelectionFeedback } from 'utils/hapticFeedback';
import { timingFeedbackDefaultConfig } from '../constants';
import { positionXWithMargin, setOriginalXYAccordingToPosition } from '../worklets/position';
const android = Platform.OS === 'android';
/* eslint-disable no-param-reassign */ // eslint doesn’t know how reanimated shared values work
// we’ve got some really long calculations here
const useLongPressGestureHandler = ({ state, data, isStarted, dotScale, pathOpacity, hapticsEnabledValue, hitSlopValue, layoutSize, dataFiltered, positionX, positionY, originalX, originalY, progress, currPeriod, originalDataSet, }) => useMemo(() => {
    // Moves the selection dot to the touch. Runs when the press starts and on every move after.
    const select = (x, gestureState, isStart) => {
        'worklet';
        state.value = gestureState;
        if (!data.value[currPeriod.value] || data.value[currPeriod.value].length === 0) {
            return;
        }
        if (!isStarted.value) {
            dotScale.value = withTiming(1);
            pathOpacity.value = withTiming(0, timingFeedbackDefaultConfig);
        }
        if (hapticsEnabledValue.value && !isStarted.value) {
            triggerChartSelectionFeedback();
        }
        isStarted.value = true;
        const eventX = positionXWithMargin(x, hitSlopValue.value, layoutSize.value.width);
        if (isStart) {
            progress.value = 1;
        }
        let idx = 0;
        let smallestDistance = +Infinity;
        for (let i = 0; i < dataFiltered.value[currPeriod.value].length; i++) {
            const distance = Math.abs(dataFiltered.value[currPeriod.value][i].x - eventX / layoutSize.value.width);
            if (distance > smallestDistance) {
                idx = i - 1;
                break;
            }
            smallestDistance = distance;
            if (i === dataFiltered.value[currPeriod.value].length - 1) {
                idx = dataFiltered.value[currPeriod.value].length - 1;
            }
        }
        let chartDotInterpolationIndex = 0;
        for (let i = 0; i < dataFiltered.value[currPeriod.value].length; i++) {
            if (dataFiltered.value[currPeriod.value][i].x > eventX / layoutSize.value.width) {
                chartDotInterpolationIndex = i;
                break;
            }
            if (i === dataFiltered.value[currPeriod.value].length - 1) {
                chartDotInterpolationIndex = dataFiltered.value[currPeriod.value].length - 1;
            }
        }
        if (idx === 0 || idx === dataFiltered.value[currPeriod.value].length - 1) {
            positionY.value = dataFiltered.value[currPeriod.value][idx].y * layoutSize.value.height;
            positionX.value = dataFiltered.value[currPeriod.value][idx].x * layoutSize.value.width;
        }
        else {
            // prev + diff over X
            positionY.value =
                (dataFiltered.value[currPeriod.value][chartDotInterpolationIndex - 1].y +
                    (dataFiltered.value[currPeriod.value][chartDotInterpolationIndex].y -
                        dataFiltered.value[currPeriod.value][chartDotInterpolationIndex - 1].y) *
                        ((eventX / layoutSize.value.width -
                            dataFiltered.value[currPeriod.value][chartDotInterpolationIndex - 1].x) /
                            (dataFiltered.value[currPeriod.value][chartDotInterpolationIndex].x -
                                dataFiltered.value[currPeriod.value][chartDotInterpolationIndex - 1].x))) *
                    layoutSize.value.height;
            positionX.value = eventX;
        }
        setOriginalXYAccordingToPosition(originalX, originalY, dataFiltered.value[currPeriod.value][idx].x, originalDataSet.value[currPeriod.value]);
    };
    // A long press gesture reports no position after it starts, so moves come from its touch events.
    return Gesture.LongPress()
        .minDuration(0)
        .maxDistance(100000)
        .shouldCancelWhenOutside(false)
        .onStart((event) => {
        select(event.x, event.state, true);
    })
        .onTouchesMove((event) => {
        if (event.state !== State.ACTIVE || event.allTouches.length === 0) {
            return;
        }
        select(event.allTouches[0].x, event.state, false);
    })
        .onFinalize((event, success) => {
        isStarted.value = false;
        state.value = event.state;
        originalX.value = '';
        originalY.value = '';
        dotScale.value = withTiming(0);
        if (android) {
            pathOpacity.value = 1;
        }
        else {
            pathOpacity.value = withTiming(1, timingFeedbackDefaultConfig);
        }
        if (success && hapticsEnabledValue.value) {
            triggerChartSelectionFeedback();
        }
    });
}, [
    currPeriod,
    data,
    dataFiltered,
    dotScale,
    hapticsEnabledValue,
    hitSlopValue,
    isStarted,
    layoutSize,
    originalDataSet,
    originalX,
    originalY,
    pathOpacity,
    positionX,
    positionY,
    progress,
    state,
]);
export default useLongPressGestureHandler;
